// src/Dashboard/pages/admin/Attendance/components/AttendanceToolbar.jsx
import React from "react";
import {
  FaCalendarAlt,
  FaExchangeAlt,
  FaSearch,
  FaSyncAlt,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";

import {
  STATUS_OPTIONS,
  getGroupName,
  getGroupSubtitle,
  getLocalDateInput,
} from "../utils/attendanceUtils";

export default function AttendanceToolbar({
  loading,
  filters,
  setFilter,
  selectedGroup,
  totalCount,
  filteredCount,
  onRefresh,
  onChangeGroup,
}) {
  const resetFilters = () => {
    setFilter("query", "");
    setFilter("selectedDate", getLocalDateInput());
    setFilter("statutInscription", "");
    setFilter("hideStopped", true);
    setFilter("onlyPresent", false);
    setFilter("onlyAbsent", false);
    setFilter("sortByName", "name_asc");
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-950 dark:text-white">
            Appel en cours
          </h2>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {filteredCount} adhérent{filteredCount > 1 ? "s" : ""} affiché
            {filteredCount > 1 ? "s" : ""} sur {totalCount}.
          </p>

          {selectedGroup && (
            <div className="mt-3 rounded-2xl bg-orange-50 px-4 py-3 text-sm text-orange-800 ring-1 ring-orange-100 dark:bg-orange-500/10 dark:text-orange-200 dark:ring-orange-500/20">
              <p className="font-bold">{getGroupName(selectedGroup)}</p>

              {getGroupSubtitle(selectedGroup) && (
                <p className="mt-1 text-xs opacity-80">
                  {getGroupSubtitle(selectedGroup)}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onChangeGroup}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaExchangeAlt />
            Changer de groupe
          </button>

          <button
            type="button"
            onClick={onRefresh}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
            Actualiser
          </button>

          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:-translate-y-0.5 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-3 lg:grid-cols-[minmax(0,1.3fr)_190px_220px_190px]">
        <label className="relative block">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="search"
            value={filters.query}
            onChange={(event) => setFilter("query", event.target.value)}
            placeholder="Rechercher nom, prénom, licence..."
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-orange-500/10"
          />
        </label>

        <label className="relative block">
          <FaCalendarAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="date"
            value={filters.selectedDate}
            onChange={(event) => setFilter("selectedDate", event.target.value)}
            disabled={loading}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-orange-500/10"
          />
        </label>

        <select
          value={filters.statutInscription}
          onChange={(event) => setFilter("statutInscription", event.target.value)}
          disabled={loading}
          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-orange-500/10"
        >
          {STATUS_OPTIONS.map((status) => (
            <option key={status || "all"} value={status}>
              {status || "Tous les statuts"}
            </option>
          ))}
        </select>

        <select
          value={filters.sortByName}
          onChange={(event) => setFilter("sortByName", event.target.value)}
          disabled={loading}
          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:ring-orange-500/10"
        >
          <option value="name_asc">Nom A → Z</option>
          <option value="name_desc">Nom Z → A</option>
        </select>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <FilterChip
          active={filters.hideStopped}
          label="Masquer les arrêts"
          onClick={() => setFilter("hideStopped", !filters.hideStopped)}
        />

        <FilterChip
          active={filters.onlyPresent}
          label="Présents"
          icon={<FaUserCheck />}
          tone="emerald"
          onClick={() => {
            setFilter("onlyPresent", !filters.onlyPresent);
            setFilter("onlyAbsent", false);
          }}
        />

        <FilterChip
          active={filters.onlyAbsent}
          label="Absents"
          icon={<FaUserTimes />}
          tone="rose"
          onClick={() => {
            setFilter("onlyAbsent", !filters.onlyAbsent);
            setFilter("onlyPresent", false);
          }}
        />
      </div>
    </section>
  );
}

function FilterChip({ active, onClick, label, icon, tone = "orange" }) {
  const activeClass =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
      : tone === "rose"
      ? "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60"
      : "bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-900/20 dark:text-orange-200 dark:ring-orange-800/60";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-xs font-bold ring-1 transition ${
        active
          ? activeClass
          : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700 dark:hover:bg-slate-700"
      }`}
    >
      {icon}
      <span
        className={`h-2 w-2 rounded-full ${
          active ? "bg-current" : "bg-slate-400"
        }`}
      />
      {label}
    </button>
  );
}